import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { getDoctors } from "~/services/antecipebackoffice/doctors";
import { STATUS_SHORT_LABEL } from "~/utils/mergeSelection";
import {
  DEFAULT_GROUP_FILTERS,
  DEFAULT_GROUP_STATUS,
  GROUP_STATUS_KEYS,
  GROUP_STATUS_SLUGS,
  groupFiltersToSearch,
  parseGroupFilters,
} from "~/utils/packageFilters";
import { useAutoApplyFilters } from "~/shared/ui/filters/useAutoApplyFilters";

// Ruling P10: rótulos dos chips vêm do mesmo mapa de status curtos (sem duplicar textos).
const STATUS_CHIP_LABEL = Object.fromEntries(GROUP_STATUS_KEYS.map((key) => [key, STATUS_SHORT_LABEL[GROUP_STATUS_SLUGS[key]]]));

function DoctorFilter({ doctorId, doctorLabel, onChange }) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    const term = input.trim();
    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      getDoctors({ limit: 20, offset: 0 }, { fullName: term })
        .then((response) => {
          if (alive) setOptions(response.data.data.map((d) => ({ id: String(d.id), label: `${d.name} · CRM ${d.crm}/${d.crm_uf}` })));
        })
        .catch(() => {
          if (alive) setOptions([]);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [input]);

  const pickedLabel = picked && picked.id === doctorId ? picked.label : "";
  const value = doctorId ? { id: doctorId, label: doctorLabel || pickedLabel || `Médico #${doctorId}` } : null;

  return (
    <Autocomplete
      size="small"
      sx={{ width: 280 }}
      options={options}
      value={value}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      onInputChange={(event, v, reason) => {
        if (reason === "input") setInput(v);
      }}
      onChange={(event, option) => {
        setPicked(option);
        onChange(option ? option.id : "");
      }}
      noOptionsText={input.trim().length < 2 ? "Digite 2 letras do nome" : "Nenhum médico"}
      renderInput={(params) => <TextField {...params} label="Médico" />}
    />
  );
}

// Só os campos de filtro: página e limite ficam com a lista (não disparam a barra).
const pick = ({ page, limit, ...rest }) => rest;
const FILTER_DEFAULTS = pick(DEFAULT_GROUP_FILTERS);
// Mesma normalização da URL (dígitos no nº, trim na unidade): o que se digita não some quando a URL volta.
const normalize = (d) => pick(parseGroupFilters(groupFiltersToSearch({ ...DEFAULT_GROUP_FILTERS, ...d })));
const TEXT_FIELDS = ["id", "hospitalName"];
const paymentModeOf = (paymentDate) => (paymentDate === "none" ? "none" : paymentDate ? "date" : "");

// Decisão U1: aplica sozinho (seletores/chips/datas/switch 300 ms, texto 400 ms ou Enter); sem "Aplicar filtros".
export default function PackageGroupsFilterBar({ value, companies, doctorLabel, onChange, onClear }) {
  const { draft, setField, update, bindText, clear, hasActive } = useAutoApplyFilters({
    value: pick(value),
    defaults: FILTER_DEFAULTS,
    onApply: (next) => onChange({ ...normalize(next), page: 1 }),
    onClear,
    textFields: TEXT_FIELDS,
    normalize,
  });
  const [paymentMode, setPaymentMode] = useState(paymentModeOf(draft.paymentDate));
  useEffect(() => {
    setPaymentMode(paymentModeOf(draft.paymentDate));
  }, [draft.paymentDate]);

  const toggleStatus = (key) =>
    update((d) => {
      const has = d.status.includes(key);
      const next = has ? d.status.filter((k) => k !== key) : GROUP_STATUS_KEYS.filter((k) => k === key || d.status.includes(k));
      return { ...d, status: next.length ? next : DEFAULT_GROUP_STATUS };
    });

  return (
    <Box role="search" aria-label="Filtros dos pacotes" sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", my: 1 }}>
      <TextField size="small" label="Nº do pacote" {...bindText("id")} inputProps={{ inputMode: "numeric" }} sx={{ width: 130 }} />
      <TextField select size="small" label="Empresa" value={draft.companyId} onChange={(e) => setField("companyId", e.target.value)} sx={{ minWidth: 220 }}>
        <MenuItem value="">Todas</MenuItem>
        {companies.map((c) => (
          <MenuItem key={c.id} value={String(c.id)}>{c.name}{c.active === false ? " (inativa)" : ""}</MenuItem>
        ))}
        {draft.companyId && !companies.some((c) => String(c.id) === draft.companyId) && (
          <MenuItem value={draft.companyId}>Empresa #{draft.companyId}</MenuItem>
        )}
      </TextField>
      <FormControlLabel
        control={<Switch size="small" checked={Boolean(draft.includeInactive)} onChange={(e) => setField("includeInactive", e.target.checked)} />}
        label="Incluir empresas inativas"
      />
      <DoctorFilter doctorId={draft.doctorId} doctorLabel={draft.doctorId === value.doctorId ? doctorLabel : ""} onChange={(id) => setField("doctorId", id)} />
      <TextField size="small" label="Unidade" {...bindText("hospitalName")} sx={{ width: 200 }} />
      <TextField size="small" type="date" label="Plantão de" InputLabelProps={{ shrink: true }} value={draft.shiftFrom} onChange={(e) => setField("shiftFrom", e.target.value)} />
      <TextField size="small" type="date" label="Plantão até" InputLabelProps={{ shrink: true }} value={draft.shiftTo} onChange={(e) => setField("shiftTo", e.target.value)} />
      <TextField
        select size="small" label="Pagamento do pacote" value={paymentMode} sx={{ width: 190 }}
        onChange={(e) => {
          const mode = e.target.value;
          setPaymentMode(mode);
          if (mode === "") setField("paymentDate", "");
          if (mode === "none") setField("paymentDate", "none");
        }}
      >
        <MenuItem value="">Todas</MenuItem>
        <MenuItem value="none">Sem data</MenuItem>
        <MenuItem value="date">Escolher data…</MenuItem>
      </TextField>
      {paymentMode === "date" && (
        <TextField
          size="small" type="date" label="Data de pagamento" InputLabelProps={{ shrink: true }}
          value={draft.paymentDate === "none" ? "" : draft.paymentDate}
          onChange={(e) => setField("paymentDate", e.target.value)}
        />
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }} aria-label="Status">
        <span style={{ fontSize: 12, color: "#6b7280" }}>Status:</span>
        {GROUP_STATUS_KEYS.map((key) => {
          const on = draft.status.includes(key);
          return (
            <Chip key={key} size="small" clickable disabled={Boolean(draft.id)} aria-pressed={on} color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"} label={STATUS_CHIP_LABEL[key]} onClick={() => toggleStatus(key)} />
          );
        })}
        <Chip size="small" clickable disabled={Boolean(draft.id)} aria-pressed={draft.includeMerged} variant={draft.includeMerged ? "filled" : "outlined"} label="Agrupado (encerrado)" onClick={() => setField("includeMerged", !draft.includeMerged)} />
        {draft.id && (
          // D7 / AB D20: com número, o backend ignora os chips de status e a janela de 180 dias
          <span style={{ fontSize: 12, color: "#6b7280" }}>Busca por número: mostra o pacote em qualquer status, inclusive agrupado.</span>
        )}
      </Box>
      <Chip size="small" clickable aria-pressed={draft.onlyCandidates} color={draft.onlyCandidates ? "secondary" : "default"} variant={draft.onlyCandidates ? "filled" : "outlined"} label="⧉ Candidatos a agrupar" onClick={() => setField("onlyCandidates", !draft.onlyCandidates)} />
      <Chip size="small" clickable aria-pressed={draft.onlySplitChildren} color={draft.onlySplitChildren ? "warning" : "default"} variant={draft.onlySplitChildren ? "filled" : "outlined"} label="⑂ Filho de split" onClick={() => setField("onlySplitChildren", !draft.onlySplitChildren)} />
      {hasActive && <Button onClick={clear}>Limpar filtros</Button>}
    </Box>
  );
}
